import { create } from 'zustand';
import type { WeekBlock } from '@peach/types/schedule/week-grid.js';
import type { InviterDto } from '@peach/types/tutors/create-page.dto.js';
import { DEFAULT_CLAIM_CATALOGUE } from '@peach/types/tutors/claim-page.consts.js';
import { defaultClaimHours, type BlockSet } from '@/lib/availability/blocks';
import type { ClaimAddressState } from '@/lib/page-address';

/** The five questions of `/invite`, in the order they are asked. */
export const INVITE_STEPS = ['name', 'what', 'week', 'prices', 'publish'] as const;

export interface InvitePrices {
  introPrice: number;
  introMinutes: number;
  hourPrice: number;
}

export const DEFAULT_INVITE_PRICES: InvitePrices = {
  introPrice: DEFAULT_CLAIM_CATALOGUE.introPrice,
  introMinutes: DEFAULT_CLAIM_CATALOGUE.introMinutes,
  hourPrice: DEFAULT_CLAIM_CATALOGUE.hourPrice,
};

interface InvitePageState {
  step: number;
  name: string;
  tagline: string;
  address: ClaimAddressState | null;
  /** The visitor typed the link themselves, so a name edited later no longer moves it. */
  addressTyped: boolean;
  week: BlockSet;
  prices: InvitePrices;
  /** False until a price is edited: untouched defaults send no prices, and the claim's apply. */
  priced: boolean;
  /** The creator's code from `/invite/{code}`, sent with Publish so they are the inviter. */
  inviteCode: string | null;
  /** Who that code names: their name, link and tagline are the examples in the empty fields. */
  inviter: InviterDto | null;
  go: (step: number) => void;
  setName: (name: string) => void;
  setTagline: (tagline: string) => void;
  setAddress: (address: ClaimAddressState, typed?: boolean) => void;
  setWeek: (week: BlockSet) => void;
  setPrices: (prices: InvitePrices) => void;
  setInvite: (invite: { code: string; inviter: InviterDto } | null) => void;
}

export const useInvitePage = create<InvitePageState>((set) => ({
  step: 0,
  name: '',
  tagline: '',
  address: null,
  addressTyped: false,
  week: defaultClaimHours(),
  prices: DEFAULT_INVITE_PRICES,
  priced: false,
  inviteCode: null,
  inviter: null,
  go: (step) => set({ step }),
  setName: (name) => set({ name }),
  setTagline: (tagline) => set({ tagline }),
  setAddress: (address, typed = false) =>
    set((s) => ({ address, addressTyped: s.addressTyped || typed })),
  setWeek: (week) => set({ week }),
  setPrices: (prices) => set({ prices, priced: true }),
  setInvite: (invite) =>
    set({ inviteCode: invite?.code ?? null, inviter: invite?.inviter ?? null }),
}));

/** The open ranges of a week, as the page is made from them. */
export function openBlocks(week: BlockSet): WeekBlock[] {
  return week.blocks
    .filter((b) => !week.disabledDays.has(b.day))
    .map(({ day, start, end }) => ({ day, start, end }));
}
